import { call, put, race, take, takeLatest } from 'redux-saga/effects'

import { loadMenuByStoreId, loadCateringMenuByStoreId } from '../../api/api'
import { FULFILLED, REJECTED } from '../utils'
import { DELIVERY_DROPOFF_SCHEDULE_SET } from './delivery'
import { apiSaga } from './sagas'

// ------------------------------------
// Action Types & Creators
// ------------------------------------

export const LOAD_MENU = 'foodsby/menu/LOAD_MENU'
export const LOAD_RESERVE_MENU = 'foodsby/menu/LOAD_RESERVE_MENU'
export const LOAD_MENU_SUCCESS = 'foodsby/menu/LOAD_MENU_SUCCESS'
export const LOAD_MENU_FAILURE = 'foodsby/menu/LOAD_MENU_FAILURE'

export const LOAD_CATERING_MENU = 'foodsby/menu/LOAD_CATERING_MENU'
export const LOAD_CATERING_MENU_SUCCESS = 'foodsby/menu/LOAD_CATERING_MENU_SUCCESS'
export const LOAD_CATERING_MENU_FAILURE = 'foodsby/menu/LOAD_CATERING_MENU_FAILURE'

export const SELECT_MENU_ITEM = 'foodsby/menu/SELECT_MENU_ITEM'
export const CLEAR_MENU_ITEM = 'foodsby/menu/CLEAR_MENU_ITEM'

export const loadMenuStart = deliveryDropoffId => {
  return {
    payload: {
      deliveryDropoffId,
      isMenuLoading: true,
    },
    type: LOAD_MENU,
  }
}

export const loadReserveMenuStart = storeId => {
  return {
    payload: {
      storeId,
      isMenuLoading: true,
    },
    type: LOAD_RESERVE_MENU,
  }
}

export const loadMenuSuccess = menu => {
  return {
    payload: { isMenuLoading: false, menu },
    type: LOAD_MENU_SUCCESS,
  }
}

export const loadMenuFailure = error => {
  return {
    error,
    payload: { isMenuLoading: false },
    type: LOAD_MENU_FAILURE,
  }
}

export const loadCateringMenuStart = storeId => {
  return {
    payload: {
      storeId,
      isMenuLoading: true,
    },
    type: LOAD_CATERING_MENU,
  }
}

export const selectMenuItem = menuItemId => {
  return {
    payload: { menuItemId },
    type: SELECT_MENU_ITEM,
  }
}

export const clearMenuItem = () => {
  return {
    type: CLEAR_MENU_ITEM,
  }
}

// ------------------------------------
// Action Handlers
// ------------------------------------
const ACTION_HANDLERS = {
  [CLEAR_MENU_ITEM]: state => {
    return {
      ...state,
      menuItemId: undefined,
    }
  },
  [LOAD_MENU]: (state, action) => {
    const {
      payload: { deliveryDropoffId },
    } = action
    return {
      ...state,
      deliveryDropoffId: deliveryDropoffId,
      isMenuLoading: true,
    }
  },
  [LOAD_RESERVE_MENU]: (state, action) => {
    const {
      payload: { storeId },
    } = action
    return {
      ...state,
      storeId,
      isMenuLoading: true,
    }
  },
  [LOAD_MENU_FAILURE]: (state, action) => {
    const { error } = action
    return {
      ...state,
      error,
      isMenuLoading: false,
    }
  },
  [LOAD_MENU_SUCCESS]: (state, action) => {
    const {
      payload: { menu },
    } = action
    return {
      ...state,
      isMenuLoading: false,
      menu,
      error: undefined,
    }
  },
  [LOAD_CATERING_MENU]: (state, action) => {
    const {
      payload: { storeId },
    } = action
    return {
      ...state,
      storeId: storeId,
      isMenuLoading: true,
    }
  },
  [LOAD_CATERING_MENU_FAILURE]: (state, action) => {
    const { error } = action
    return {
      ...state,
      error,
      isMenuLoading: false,
    }
  },
  [LOAD_CATERING_MENU_SUCCESS]: (state, action) => {
    const {
      payload: { menu },
    } = action
    return {
      ...state,
      isMenuLoading: false,
      menu,
      error: undefined,
    }
  },
  [SELECT_MENU_ITEM]: (state, action) => {
    const {
      payload: { menuItemId },
    } = action
    return {
      ...state,
      menuItemId: menuItemId,
    }
  },
}
// ------------------------------------
// Reducer
// ------------------------------------

const initialState = {
  isMenuLoading: true,
  menu: {
    subMenus: [],
  },
}

export default function menu(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}

// ------------------------------------
// Sagas
// ------------------------------------

// A menu request without a store id resolves to `/store/undefined/menu`, which
// fails and clobbers the menu state of the request that was actually valid.
export function* loadMenuForStore(loader, storeId) {
  if (storeId === undefined || storeId === null) {
    yield put(loadMenuFailure(new Error('Cannot load a menu without a store id')))
    return
  }
  yield call(apiSaga, loader, [storeId], loadMenuSuccess, loadMenuFailure)
}

// takeLatest, not while/take: the store id arrives on a later
// DELIVERY_DROPOFF_SCHEDULE_SET, so a second LOAD_MENU dispatched while we are
// still waiting used to be dropped on the floor, leaving every following load
// paired with the wrong dropoff. Restarting cancels the stale wait instead.
export function* watchLoadMenu() {
  yield takeLatest(LOAD_MENU, function* loadMenu() {
    try {
      let [result, error] = yield race([
        take(FULFILLED(DELIVERY_DROPOFF_SCHEDULE_SET)),
        take(REJECTED(DELIVERY_DROPOFF_SCHEDULE_SET)),
      ])
      if (error) {
        throw error
      }
      yield call(loadMenuForStore, loadMenuByStoreId, result?.payload?.storeId)
    } catch (error) {
      yield put(loadMenuFailure(error))
    }
  })
}

// Reserve and catering carry the store id on the action itself, so they differ
// only in what starts them and where they load from.
const watchLoadMenuFromAction = (actionType, loader) =>
  function* watchLoadMenuByStoreId() {
    yield takeLatest(actionType, function* loadMenuByAction(action) {
      try {
        yield call(loadMenuForStore, loader, action?.payload?.storeId)
      } catch (error) {
        yield put(loadMenuFailure(error))
      }
    })
  }

export const watchLoadReserveMenu = watchLoadMenuFromAction(LOAD_RESERVE_MENU, loadMenuByStoreId)

export const watchLoadCateringMenu = watchLoadMenuFromAction(
  LOAD_CATERING_MENU,
  loadCateringMenuByStoreId,
)
